import {
  Col,
  DatePicker,
  Empty,
  Flex,
  Form,
  Progress,
  Row,
  Spin,
  Table,
  Tooltip,
} from "antd";
import React, { useEffect, useState, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import { Button, Card } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import FormSelect from "../form-components/SelectComponent";
import SearchInput from "../form-components/SearchComponent";
import { LoadingOutlined, SearchOutlined } from "@ant-design/icons";
import Export from "../../components/export/Export";
import dayjs from "dayjs";
import miscService from "../../api/misc/misc";
import { InfoCircleOutlined } from "@ant-design/icons";
import { convertToDates } from "../../utils/convertions";
import FormMultiSelect from "../form-components/MultiSelectComponent";
import { uploadApis } from "../../api/Uploads";

const { RangePicker } = DatePicker;
type WidgetType = {
  title: string;
  key: string;
  color: string;
  percentage: number;
  value: number;
};
type SelectOptions = {
  label: string;
  value: string;
};
type SelectFilterType = {
  title: string;
  placeholder?: string;
  options: SelectOptions[];
  onChange?: (value: any) => void;
  selectionMode?: "multiple" | "single";
};
const TransactionFlowDefaultTemplate: React.FC<any> = ({
  title,
  moduleKey,
  widget,
  columns,
  sideNavigation,
  selectFilters,
  previousPath,
  extra,
  topRightJSX,
  refresh,
  components,
  rowSelection,
  form,
  projectId,
}: {
  title: string;
  moduleKey: string;
  columns: any[];
  sideNavigation?: any;
  widget?: {
    isActive: boolean;
    list: WidgetType[];
  };
  selectFilters?: SelectFilterType[];
  previousPath?: string;
  topRightJSX?: any;
  extra?: {
    id?: any;
    tableScrollX?: number;
    dateRange?: boolean;
    excelExport?: boolean;
    bulkUpload?: boolean;
    dataFilterTootip?: string;
  };
  refresh?: boolean;
  components?: any;
  rowSelection?: any;
  form?: any;
  projectId?: string;
}) => {
  const methods = useForm({
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;
  const [customDateRange, setCustomDateRange] = useState<any>();
  const [dataSource, setDataSource] = useState<any>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    defaultCurrent: 1,
    defaultPageSize: 10,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "30", "40", "50"],

    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [screenSize, setScreenSize] = useState(0);

  const [searchQuery, setsearchQuery] = useState(null);

  const selectFilter1 = watch("selectFilter1");
  const selectFilter2 = watch("selectFilter2");
  const selectFilter3 = watch("selectFilter3");
  const dateRange = watch("dateRange");
  const isFetchingRef = useRef(false);
  //---------------------------------------- Functions Start ----------------------------------------

  const fetchData = async (params: any) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setLoading(true);
    try {
      const {
        current,
        sortOrder,
        pageSize,
        direction,
        pageDiff,
        paginationId,
        searchQuery,
        selectFilter1,
        selectFilter2,
        selectFilter3,
        dateRange,
        customDateRange,
        projectId,
      } = params;
      if (
        extra?.dateRange &&
        ((dateRange == "Custom Date" && customDateRange == undefined) ||
          dateRange == undefined)
      )
        return;
      const { from, to }: any =
        dateRange !== undefined && convertToDates(dateRange, customDateRange);
      let apiResponse: any = {};
      switch (moduleKey) {
        case "bulk-upload-list":
          apiResponse = await uploadApis.fetchWBSWithPagination({
            sortOrder,
            pageSize,
            direction,
            pageDiff,
            paginationId,
            searchQuery,
            projectIds: projectId,
          });
          break;
        case "excelexport-requested-list":
          apiResponse =
            await miscService.fetchExcelExportRequestedListWithPagination({
              sortOrder,
              pageSize,
              direction,
              pageDiff,
              paginationId,
              searchQuery,
              applicationType: "PMS",
              projectIds: projectId,
            });
          break;
        default:
          alert("Invalid key " + moduleKey);
      }
      const { data, status } = apiResponse;
      if (status) {
        const { list, count } =
          data || apiResponse.response.data || apiResponse.response;
        setDataSource(list);
        setPagination((prev) => ({
          ...prev,
          total: Number(count),
        }));
      }
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const tableOnChange = async (
    newPagination: any,
    filters: any,
    sorter: any
  ) => {
    const apiParams = {
      oldPage: pagination.current,
      current: newPagination.current?.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize,
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
    };

    if (sorter.order) {
      apiParams.sortOrder = sorter.order === "ascend" ? "ASC" : "DESC";
    } else if (newPagination.pageSize != pagination.pageSize) {
      // if page size is changed
      apiParams.pageSize = newPagination.pageSize;
      const _currentPage =
        Math.floor(
          ((Number(pagination.current) - 1) * Number(pagination.pageSize)) /
            Number(newPagination.pageSize)
        ) + 1; // calculating new current page based on new page size
      apiParams.current = _currentPage;
      apiParams.pageDiff = _currentPage - 1;
    } else {
      if (newPagination.current != pagination.current) {
        apiParams.direction =
          newPagination.current > pagination.current
            ? "F" // Forward
            : "B"; // Backward
        apiParams.paginationId =
          apiParams.direction == "F"
            ? dataSource[dataSource.length - 1]?.paginationId // Last item ID for backward direction
            : dataSource[0]?.paginationId; // First item ID for forward direction

        apiParams.pageDiff =
          Math.abs(newPagination.current - pagination.current) - 1;
      }
    }

    setPagination({
      ...pagination,
      current: apiParams.current,
      pageSize: apiParams.pageSize,
    });
    await fetchData({
      ...apiParams,
      selectFilter1,
      selectFilter2,
      selectFilter3,
      dateRange,
      customDateRange,
      projectId,
    });
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
  };

  //---------------------------------------- Functions End----------------------------------------

  const refreshData = () => {
    if (isFetchingRef.current) return;
    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: pagination.current - 1,
      paginationId: null,
      searchQuery,
      dateRange,
      customDateRange,
      projectId,
    });
  };
  useEffect(() => {
    if (extra?.dateRange) {
      setValue("dateRange", "Quarter to Date");
    }
    refreshData();
  }, [projectId]);

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
    };
  }, []);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const shouldFetch =
      refresh !== undefined ||
      selectFilter1 !== undefined ||
      selectFilter2 !== undefined ||
      selectFilter3 !== undefined ||
      dateRange !== undefined ||
      customDateRange !== undefined ||
      projectId !== undefined;

    if (!shouldFetch) return;
    setPagination((prev) => ({
      ...prev,
      current: 1,
    }));

    fetchData({
      oldPage: 1,
      current: pagination.current.toString(),
      sortOrder: "DESC",
      pageSize: pagination.pageSize.toString(),
      direction: "P",
      pageDiff: 0,
      paginationId: null,
      searchQuery,
      selectFilter1,
      selectFilter2,
      selectFilter3,
      dateRange,
      customDateRange,
      projectId,
    });
  }, [
    refresh,
    selectFilter1,
    selectFilter2,
    selectFilter3,
    dateRange,
    customDateRange,
    projectId,
  ]);

  const tableScrollX = extra?.tableScrollX
    ? Math.min(extra.tableScrollX, screenSize - (sideNavigation ? 330 : 100))
    : screenSize - (sideNavigation ? 330 : 100);

  return (
    <>
      <Head title={title} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            {sideNavigation}
            <div className="card-inner card-inner-lg">
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent className="w-100">
                    <BlockTitle
                      tag="h4"
                      className="d-flex justify-content-between align-items-center  w-100"
                    >
                      <span>{title}</span>
                    </BlockTitle>
                  </BlockHeadContent>
                  <BlockHeadContent>
                    <div className="toggle-wrap nk-block-tools-toggle">
                      <Button
                        className={`btn-icon btn-trigger toggle-expand me-n1`}
                      >
                        <Icon name="menu-alt-r"></Icon>
                      </Button>
                      <div className="toggle-expand-content">
                        <ul className="nk-block-tools g-3">
                          {extra?.excelExport && (
                            <li>
                              <Export
                                data={{
                                  sortOrder: "DESC",
                                  pageSize: 100,
                                  direction: "P",
                                  pageDiff: 0,
                                  searchQuery,
                                  selectFilter1,
                                  selectFilter2,
                                  selectFilter3,
                                  dateRange:
                                    dateRange &&
                                    convertToDates(dateRange, customDateRange),
                                  customDateRange,
                                  // projectIds: selectFilter1,
                                  projectIds: projectId,
                                  userIds: selectFilter3,
                                }}
                                moduleKey={moduleKey}
                              />
                            </li>
                          )}
                          {/* {extra?.bulkUpload && (
                            <li>
                              <BulkUpload
                                moduleKey={moduleKey}
                              />
                            </li>
                          )} */}
                          <li className="nk-block-tools-opt">{topRightJSX}</li>
                        </ul>
                      </div>
                    </div>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>
              <Block>
                {widget?.isActive && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      width: "100%",
                    }}
                  >
                    {widget?.list.map((widget, index) => (
                      <div
                        key={index}
                        style={{
                          border: "1px solid #dbdfea",
                          padding: "15px",
                          borderRadius: "8px",
                          width: "calc(20% - 10px)", // Ensure widgets are 20% of the width
                          minWidth: "150px", // Ensure a minimum width for smaller screens
                          marginBottom: "10px",
                        }}
                      >
                        <div style={{ marginBottom: "20px" }}>
                          <h6 style={{ margin: 0 }}>{widget.title}</h6>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <Progress
                              type="circle"
                              percent={
                                Number(widget?.percentage)
                                  ?.toFixed(2)
                                  ?.toString() as any
                              }
                              width={50}
                              strokeColor={widget.color}
                              format={(percent) => `${Number(percent)}%`}
                            />
                          </div>
                          <div style={{ flex: 1, textAlign: "center" }}>
                            <p style={{ fontSize: "24px", margin: 0 }}>
                              {widget.value}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Block>
              <FormProvider {...methods}>
                <form className="w-100 d-flex align-items-center justify-content-start">
                  {(selectFilters && selectFilters?.length > 0) ||
                  extra?.dateRange ? (
                    <Row gutter={[16, 16]} className="w-75 mb-2">
                      <Col xs={24} sm={24} md={18} lg={18} xl={18}>
                        <Row gutter={[16, 16]}>
                          {selectFilters?.map(
                            (filter: SelectFilterType, index: any) => (
                              <Col
                                xs={24}
                                sm={12}
                                md={8}
                                lg={8}
                                xl={8}
                                key={"filters" + index}
                              >
                                {filter?.selectionMode !== "multiple" ? (
                                  <FormSelect
                                    name={`selectFilter${index + 1}`}
                                    label={filter?.title}
                                    // required={false}
                                    hideRequiredMark={true}
                                    placeholder={
                                      filter?.placeholder || filter?.title
                                    }
                                    options={filter?.options}
                                    onChange={(e: any) => {
                                      if (
                                        typeof filter.onChange === "function"
                                      ) {
                                        filter.onChange(e);
                                      }
                                    }}
                                  />
                                ) : (
                                  <FormMultiSelect
                                    name={`selectFilter${index + 1}`}
                                    label={filter?.title}
                                    // required={false}
                                    // hideRequiredMark={true}
                                    placeholder={
                                      filter?.placeholder || filter?.title
                                    }
                                    options={filter?.options}
                                    onChange={(e: any) => {
                                      if (
                                        typeof filter.onChange === "function"
                                      ) {
                                        filter.onChange(e);
                                      }
                                    }}
                                  />
                                )}
                              </Col>
                            )
                          )}
                          {extra?.dateRange && (
                            <Col xs={24} sm={12} md={8} lg={8} xl={8}>
                              <FormSelect
                                name="dateRange"
                                required={true}
                                hideRequiredMark={true}
                                defaultValue={"Quarter to Date"}
                                // label=""
                                label={
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    Select Date Range
                                    <Tooltip title={extra?.dataFilterTootip}>
                                      <InfoCircleOutlined
                                        style={{
                                          marginLeft: 8,
                                          fontSize: 12,
                                        }}
                                      />
                                    </Tooltip>
                                  </div>
                                }
                                // optionLabel="Date Range"
                                // placeholder="Select Date Range"
                                options={[
                                  {
                                    value: "Month to Date",
                                    label: "Month to Date",
                                  },
                                  {
                                    value: "Quarter to Date",
                                    label: "Quarter to Date",
                                  },
                                  {
                                    value: "Year to Date",
                                    label: "Year to Date",
                                  },
                                  {
                                    value: "Custom Date",
                                    label: "Custom Date",
                                  },
                                ]}
                              />
                              {dateRange === "Custom Date" && (
                                <RangePicker
                                  value={
                                    customDateRange
                                      ? [
                                          dayjs(customDateRange[0].toDate()),
                                          dayjs(customDateRange[1].toDate()),
                                        ]
                                      : null
                                  }
                                  onChange={(dates) => {
                                    if (dates && dates[0] && dates[1]) {
                                      setCustomDateRange([dates[0], dates[1]]);
                                    } else {
                                      setCustomDateRange(null);
                                    }
                                  }}
                                  style={{ width: "100%", marginTop: 8 }}
                                />
                              )}
                            </Col>
                          )}
                        </Row>
                      </Col>
                    </Row>
                  ) : null}

                  <Col xs={24} sm={24} md={24} lg={24} xl={24} className="mb-2">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          minWidth: screenSize < 600 ? "160px" : "350px",
                          maxWidth:
                            screenSize >= 1200
                              ? "100%"
                              : screenSize < 900
                              ? "600px"
                              : "900px",
                          marginRight: screenSize < 800 ? 8 : 0,
                        }}
                      >
                        <SearchInput
                          name="search"
                          placeholder="Search..."
                          width="100%"
                          prefixIcon={<SearchOutlined />}
                          onDebouncedSearch={async (value: any) => {
                            setsearchQuery(value);
                            setPagination({
                              ...pagination,
                              current: 1,
                            });
                            await fetchData({
                              oldPage: 1,
                              current: 1,
                              sortOrder: "DESC",
                              pageSize: pagination.pageSize.toString(),
                              direction: "P",
                              pageDiff: 0,
                              paginationId: null,
                              searchQuery: value,
                              selectFilter1,
                              selectFilter2,
                              selectFilter3,
                              dateRange,
                              customDateRange,
                              projectId,
                            });
                          }}
                          debounceDelay={300} // Adjust delay as needed
                        />
                      </div>
                      <span
                        onClick={refreshData}
                        // title="Refresh"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          cursor: "pointer",
                          marginLeft: screenSize < 600 ? 8 : 16,
                          fontSize: 22,
                          color: "#556080",
                        }}
                      >
                        <Icon name="redo" />
                      </span>
                    </div>
                  </Col>
                </form>
              </FormProvider>
              <div className="content clearfix">
                {loading == true ? (
                  <>
                    <Flex align="center" gap="middle" className="pageLoader">
                      <Spin
                        indicator={<LoadingOutlined spin />}
                        className="m-auto"
                        size="large"
                      />
                    </Flex>
                  </>
                ) : dataSource?.length > 0 ? (
                  <div
                    style={{
                      width: "100%",
                      maxWidth:
                        screenSize - (sideNavigation ? 330 : 100) + "px",
                      margin: "0 auto",
                      overflowX: "auto",
                    }}
                  >
                    <Form form={form} component={false}>
                      <Table
                        className="customTable"
                        columns={columns}
                        rowSelection={rowSelection}
                        dataSource={dataSource}
                        pagination={pagination}
                        onChange={tableOnChange}
                        components={components}
                        bordered
                        size="middle"
                        scroll={{
                          x: tableScrollX,
                        }}
                      />
                    </Form>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px",
                    }}
                  >
                    <Empty description="No Data Found " />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      </Content>
    </>
  );
};

export default TransactionFlowDefaultTemplate;
